import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Loader2,
  ShieldCheck,
  ShieldX,
  FileClock,
  RefreshCw,
  Send,
  Plus,
  Check,
  X,
  FileText,
} from "lucide-react";
import {
  getProcessDetail,
  changeStatus,
  addDocument,
  updateDocStatus,
} from "@/lib/portalApi";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/portal/StatusBadge";
import {
  PROCESS_STATUSES,
  DOC_STATUS_STYLES,
  formatDateTime,
} from "@/components/portal/portalConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageThread } from "@/components/common/MessageThread";
import { getProcessMessages, sendProcessMessageFile, markPortalRead, markAlertsRead } from "@/lib/portalApi";
import { uploadProducedDocument, addDocument as apiAddDocument, deleteDocument } from "@/lib/portalApi";
import { assignProcess, getPortalUsers } from "@/lib/portalApi";
import { Trash2, Upload, ExternalLink } from "lucide-react";
import { fileHref } from "@/components/account/accountConstants";
import { WebApplicationBanner } from "@/components/portal/case/WebApplicationBanner";
import { BillingPanel } from "@/components/portal/case/BillingPanel";
import { openAuthed } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";
import { AiAgentPanel } from "@/components/portal/case/AiAgentPanel";

const selectClasses =
  "h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-venka-green-light/40";

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-100 text-right break-words">{value || "\u2014"}</span>
  </div>
);

export const PortalProcessDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();

  const [data, setData] = useState(null);
  const [billingKey, setBillingKey] = useState(0);
  const { bt } = useBT();
  const [loading, setLoading] = useState(true);
  const [newDocTitle, setNewDocTitle] = useState("");
  const [immAgents, setImmAgents] = useState([]);
  const [reassignTo, setReassignTo] = useState("");

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState("");
  const [modalLocked, setModalLocked] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getProcessDetail(id);
      setData(d);
    } catch (e) {
      // handled globally
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const m = window.location.search.match(/tab=(billing|ai)/);
    if (data && m) {
      setTimeout(() => document.getElementById(m[1])?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [data]);

  useEffect(() => {
    if (isAdmin) {
      getPortalUsers("agent")
        .then((list) => setImmAgents((list || []).filter((u) => u.team === "immigration")))
        .catch(() => {});
    }
  }, [isAdmin]);

  const doReassign = async () => {
    if (!reassignTo) return;
    try {
      await assignProcess(id, reassignTo);
      toast.success(t("portal.detail.reassigned", "Case reassigned"));
      setReassignTo("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reassign");
    }
  };

  const openModal = (presetStatus, locked) => {
    setModalStatus(presetStatus || data?.process?.status || "");
    setModalLocked(!!locked);
    setNote("");
    setNoteError("");
    setModalOpen(true);
  };

  const submitStatus = async () => {
    if (!note.trim()) {
      setNoteError(t("portal.modal.noteRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await changeStatus(id, modalStatus, note.trim());
      setModalOpen(false);
      toast.success(t("portal.actions.updateStatus"));
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 403) {
        setNoteError(t("portal.modal.adminOnly"));
      } else {
        setNoteError(detail || "Error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocTitle.trim()) return;
    try {
      await addDocument(id, newDocTitle.trim());
      setNewDocTitle("");
      await load();
    } catch (e) {
      /* noop */
    }
  };

  const handleDocStatus = async (docId, status) => {
    let reason = null;
    if (status === "rejected") {
      reason = window.prompt(t("portal.doc.rejectionReason"));
      if (reason === null) return;
    }
    try {
      await updateDocStatus(docId, status, reason || undefined);
      await load();
    } catch (e) {
      /* noop */
    }
  };

  const producedRef = React.useRef(null);
  const [uploadTargetId, setUploadTargetId] = React.useState(null);

  const handleDelete = async (docId) => {
    if (!window.confirm(t("portal.doc.confirmDelete", "Delete this document?"))) return;
    try {
      await deleteDocument(docId);
      await load();
    } catch (e) {
      /* noop */
    }
  };

  const handleUploadProduced = async (fileList) => {
    const file = fileList?.[0];
    if (!file || !uploadTargetId) return;
    try {
      await uploadProducedDocument(uploadTargetId, file);
      toast.success(t("portal.doc.uploaded", "File uploaded"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadTargetId(null);
      if (producedRef.current) producedRef.current.value = "";
    }
  };

  const handleAddProduced = async () => {
    const title = window.prompt(t("portal.doc.producedTitle", "Title for the produced document (e.g. Final Visa):"));
    if (!title || !title.trim()) return;
    try {
      const doc = await apiAddDocument(id, title.trim(), "produced");
      setUploadTargetId(doc.id);
      setTimeout(() => producedRef.current?.click(), 50);
    } catch (e) {
      toast.error("Could not create document");
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-slate-400">
        <button onClick={() => navigate("/portal/processes")} className="text-venka-green-light">
          {t("portal.detail.back")}
        </button>
      </div>
    );
  }

  const { process, customer, agent, process_type, documents, audit_logs } = data;
  const details = process?.details_json || {};

  return (
    <div className="space-y-6" data-testid="portal-process-detail">
      <button
        onClick={() => navigate("/portal/processes")}
        className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"
        data-testid="detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> {t("portal.detail.back")}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {process_type?.name} · {process?.country_destination || "\u2014"}
          </h1>
          <p className="text-sm text-slate-400">{customer?.name}</p>
        </div>
        <StatusBadge status={process.status} />
      </div>

      <WebApplicationBanner details={details} />

      <div className="flex flex-wrap items-center gap-2" data-testid="case-documents-issued">
        {["pt", "en", "fr"].map((lg) => (
          <Button key={lg} size="sm" variant="outline" className="rounded-full border-slate-700 bg-slate-900 hover:bg-slate-800"
            onClick={() => openAuthed(`/portal/processes/${process.id}/confirmation.pdf?lang=${lg}`)} data-testid={`case-confirmation-${lg}`}>
            <FileText className="h-3.5 w-3.5 mr-1.5" />{bt("confirmation")} · {lg.toUpperCase()}
          </Button>
        ))}
      </div>

      {/* Action toolbar */}
      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 p-3" data-testid="action-toolbar">
        <Button
          size="sm"
          variant="outline"
          className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          onClick={() => openModal("pending_approval", true)}
          data-testid="action-request-approval"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" /> {t("portal.actions.requestApproval")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          onClick={() => openModal("in_review", true)}
          data-testid="action-request-documents"
        >
          <FileClock className="h-3.5 w-3.5 mr-1.5" /> {t("portal.actions.requestDocuments")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          onClick={() => openModal(null, false)}
          data-testid="action-update-status"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> {t("portal.actions.updateStatus")}
        </Button>
        {isAdmin && (
          <>
            <Button
              size="sm"
              className="bg-venka-green text-slate-950 hover:bg-venka-green-light"
              onClick={() => openModal("approved", true)}
              data-testid="action-approve"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> {t("portal.actions.approve")}
            </Button>
            <Button
              size="sm"
              className="bg-red-500 text-white hover:bg-red-400"
              onClick={() => openModal("rejected", true)}
              data-testid="action-reject"
            >
              <ShieldX className="h-3.5 w-3.5 mr-1.5" /> {t("portal.actions.reject")}
            </Button>
          </>
        )}
      </div>

      <AiAgentPanel processId={process.id} onChanged={() => { load(); setBillingKey((k) => k + 1); }} />

      <BillingPanel key={billingKey} processId={process.id} destination={process.country_destination} hasCustomer={!!process.customer_id} />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: customer + process info + documents */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <h2 className="font-semibold mb-3">{t("portal.detail.customer")}</h2>
              <InfoRow label={t("portal.detail.name")} value={customer?.name} />
              <InfoRow label={t("portal.detail.email")} value={customer?.email} />
              <InfoRow label={t("portal.detail.phone")} value={customer?.phone} />
              <InfoRow label={t("portal.detail.language")} value={customer?.preferred_language?.toUpperCase()} />
            </section>
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <h2 className="font-semibold mb-3">{t("portal.detail.processInfo")}</h2>
              <InfoRow label={t("portal.detail.type")} value={process_type?.name} />
              <InfoRow label={t("portal.detail.destination")} value={process?.country_destination} />
              <InfoRow label={t("portal.detail.assignedAgent")} value={agent?.name || t("portal.table.unassigned")} />
              <InfoRow label={t("portal.detail.created")} value={formatDateTime(process?.created_at, i18n.resolvedLanguage)} />
              <InfoRow label={t("portal.detail.updated")} value={formatDateTime(process?.updated_at, i18n.resolvedLanguage)} />
              {isAdmin && (
                <div className="mt-4 border-t border-slate-800 pt-3" data-testid="reassign-box">
                  <Label className="mb-1.5 block text-xs text-slate-400">{t("portal.detail.reassign", "Reassign to Immigration consultant")}</Label>
                  <div className="flex gap-2">
                    <select
                      className={selectClasses}
                      value={reassignTo}
                      onChange={(e) => setReassignTo(e.target.value)}
                      data-testid="reassign-select"
                    >
                      <option value="">{t("portal.detail.selectConsultant", "Select consultant…")}</option>
                      {immAgents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}{a.office ? ` · ${a.office}` : ""}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={doReassign}
                      disabled={!reassignTo}
                      className="bg-venka-green-light text-slate-950 hover:bg-venka-green shrink-0"
                      data-testid="reassign-button"
                    >
                      {t("portal.detail.reassignBtn", "Reassign")}
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </div>

          {Object.keys(details).length > 0 && (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <h2 className="font-semibold mb-3">{t("portal.detail.requestDetails")}</h2>
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {Object.entries(details).filter(([k, v]) => !["source", "application_id"].includes(k) && v !== null && v !== "" && !(Array.isArray(v) && !v.length)).map(([k, v]) => {
                  let shown = v;
                  if (Array.isArray(v)) shown = v.join(", ");
                  else if (v && typeof v === "object") shown = Object.keys(v).filter((x) => v[x]).map((x) => x.replace(/_/g, " ")).join(", ") || "—";
                  else if (v === true) shown = "✓";
                  return (
                    <div key={k} className="flex flex-col border-b border-slate-800 pb-2">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</dt>
                      <dd className="text-sm text-slate-200 break-words">{String(shown)}</dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          )}

          {/* Documents */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="documents-section">
            <h2 className="font-semibold mb-3">{t("portal.detail.documents")}</h2>
            <div className="space-y-2">
              {documents.length === 0 && (
                <p className="text-sm text-slate-400">{t("portal.detail.noDocuments")}</p>
              )}
              {documents.map((d) => (
                <div
                  key={d.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2"
                  data-testid={`document-${d.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-2">
                      {d.title}
                      {d.kind === "produced" && (
                        <span className="rounded-full bg-venka-blue/20 text-venka-green-light px-2 py-0.5 text-[10px] font-semibold uppercase">VENKA</span>
                      )}
                    </p>
                    {d.rejection_reason && (
                      <p className="text-xs text-red-400">{t("portal.doc.rejectionReason")}: {d.rejection_reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${DOC_STATUS_STYLES[d.status]}`}>
                      {t(`portal.docStatus.${d.status}`, d.status)}
                    </span>
                    {d.file_url && (
                      <a
                        href={fileHref(d.file_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-venka-green-light hover:text-white"
                        title={t("portal.doc.view", "View file")}
                        data-testid={`doc-view-${d.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      onClick={() => { setUploadTargetId(d.id); setTimeout(() => producedRef.current?.click(), 30); }}
                      className="text-slate-300 hover:text-white"
                      title={t("portal.doc.upload", "Upload file")}
                      data-testid={`doc-upload-${d.id}`}
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDocStatus(d.id, "approved")}
                      className="text-venka-green-light hover:text-white"
                      title={t("portal.doc.approve")}
                      data-testid={`doc-approve-${d.id}`}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDocStatus(d.id, "rejected")}
                      className="text-red-400 hover:text-red-300"
                      title={t("portal.doc.reject")}
                      data-testid={`doc-reject-${d.id}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(d.id)}
                      className="text-slate-400 hover:text-red-300"
                      title={t("portal.doc.delete", "Delete")}
                      data-testid={`doc-delete-${d.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Input
                value={newDocTitle}
                onChange={(e) => setNewDocTitle(e.target.value)}
                placeholder={t("portal.detail.docTitlePlaceholder")}
                className="bg-slate-950 border-slate-700"
                data-testid="add-document-input"
              />
              <Button
                onClick={handleAddDocument}
                className="bg-venka-green-light text-slate-950 hover:bg-venka-green shrink-0"
                data-testid="add-document-button"
              >
                <Plus className="h-4 w-4 mr-1" /> {t("portal.detail.add")}
              </Button>
              <Button
                variant="outline"
                onClick={handleAddProduced}
                className="border-slate-700 bg-slate-900 hover:bg-slate-800 shrink-0"
                data-testid="upload-produced-button"
              >
                <Upload className="h-4 w-4 mr-1" /> {t("portal.detail.uploadProduced", "Upload produced")}
              </Button>
            </div>
            <input
              ref={producedRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => handleUploadProduced(e.target.files)}
              data-testid="produced-file-input"
            />
          </section>

          <MessageThread
            title="Messages"
            currentUserId={user?.id}
            listFn={() => getProcessMessages(id)}
            sendFn={(text, file) => sendProcessMessageFile(id, text, file)}
            markReadFn={() => Promise.all([markPortalRead(id), markAlertsRead({ process_id: id }).catch(() => {})])}
          />
        </div>

        {/* Right: history timeline */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="history-section">
          <h2 className="font-semibold mb-4">{t("portal.detail.history")}</h2>
          {audit_logs.length === 0 && (
            <p className="text-sm text-slate-400">{t("portal.detail.noHistory")}</p>
          )}
          <ol className="relative border-l border-slate-800 ml-1 space-y-5">
            {audit_logs.map((a) => (
              <li key={a.id} className="ml-4" data-testid={`audit-${a.id}`}>
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-venka-green-light border border-slate-950" />
                <div className="flex items-center gap-2 flex-wrap">
                  {a.previous_status && <StatusBadge status={a.previous_status} />}
                  <span className="text-slate-500 text-xs">&rarr;</span>
                  {a.new_status && <StatusBadge status={a.new_status} />}
                </div>
                {a.comment && <p className="text-sm text-slate-200 mt-1.5">{a.comment}</p>}
                <p className="text-[11px] text-slate-500 mt-1">
                  {a.actor_name ? `${a.actor_name} \u00b7 ` : ""}
                  {formatDateTime(a.timestamp, i18n.resolvedLanguage)}
                </p>
              </li>
            ))}
          </ol>
        </section>
      </div>

      {/* Status change modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle>{t("portal.modal.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">{t("portal.modal.statusLabel")}</Label>
              <select
                className={selectClasses}
                value={modalStatus}
                disabled={modalLocked}
                onChange={(e) => setModalStatus(e.target.value)}
                data-testid="modal-status-select"
              >
                {PROCESS_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`portal.status.${s}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="mb-1.5 block">{t("portal.modal.noteLabel")}</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("portal.modal.notePlaceholder")}
                className="bg-slate-950 border-slate-700 min-h-[100px]"
                data-testid="modal-note-input"
              />
              {noteError && <p className="text-sm text-red-400 mt-1.5" data-testid="modal-error">{noteError}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              className="text-slate-300 hover:text-white"
              data-testid="modal-cancel"
            >
              {t("portal.modal.cancel")}
            </Button>
            <Button
              onClick={submitStatus}
              disabled={submitting}
              className="bg-venka-green-light text-slate-950 hover:bg-venka-green-light"
              data-testid="modal-confirm"
            >
              {submitting ? t("portal.modal.submitting") : t("portal.modal.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalProcessDetail;
